import { createContext, useContext, useState, useEffect } from 'react';
import translations from './translations';

const LanguageContext = createContext();

function resolveKey(obj, path) {
  return path.split('.').reduce((acc, part) => (acc ? acc[part] : undefined), obj);
}

// Replace {{var}} with provided values
function interpolate(str, vars = {}) {
  return str.replace(/{{\s*([^{}\s]+)\s*}}/g, (_, key) => {
    const v = vars[key];
    return v === undefined || v === null ? '' : String(v);
  });
}

// Handle very small subset of ICU plural: {count, plural, one {...} other {...}}
function renderPluralIfNeeded(str, vars = {}) {
  // Match if the whole string is a plural block
  const m = str.match(/^\s*\{(\w+),\s*plural,\s*one\s*\{([^}]*)\}\s*other\s*\{([^}]*)\}\s*\}\s*$/);
  if (!m) return str;

  const [, varName, oneText, otherText] = m;
  const rawCount = vars[varName];
  const n = typeof rawCount === 'number' ? rawCount : parseFloat(rawCount);

  // If count not supplied or NaN, just return original string
  if (!Number.isFinite(n)) return str;

  const chosen = (n === 1) ? oneText : otherText;
  // Replace # with the number, then run interpolation in case there are other {{vars}} inside
  return interpolate(chosen.replace(/#/g, String(n)), vars);
}

export const LanguageProvider = ({ children }) => {
  const [language, setLanguage] = useState(() => {
    try {
      return localStorage.getItem('airvoy-language') || 'en';
    } catch {
      return 'en';
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem('airvoy-language', language);
    } catch (error) {
      console.warn('Failed to save language preference:', error);
    }
  }, [language]);

  // t now accepts an optional params object
  const t = (key, params = {}) => {
    const langPack = translations[language] || {};
    let value = resolveKey(langPack, key);

    // Fallback: try English if missing in current language
    if (value === undefined) {
      value = resolveKey(translations.en || {}, key);
    }

    if (typeof value === 'string') {
      // First handle simple ICU plural if the value is exactly a plural block
      let out = renderPluralIfNeeded(value, params);
      // Then always run {{var}} interpolation
      out = interpolate(out, params);
      return out;
    }

    // If array/object or missing, return as-is (or the key if missing)
    return value ?? key;
  };

  const tArray = (key) => {
    const val = t(key);
    return Array.isArray(val) ? val : [];
  };

  const changeLanguage = (newLanguage) => {
    if (translations[newLanguage]) setLanguage(newLanguage);
  };

  return (
    <LanguageContext.Provider value={{ language, changeLanguage, t, tArray }}>
      {children}
    </LanguageContext.Provider>
  );
}; 

export const useTranslation = () => {
  const context = useContext(LanguageContext);
  if (!context) throw new Error('useTranslation must be used within a LanguageProvider');
  return context;
};
